import { useEffect, useMemo, useRef } from 'react';
import { useForm, useFieldArray, Controller, useWatch, type UseFormGetValues } from 'react-hook-form';
import Editor from '@monaco-editor/react';
import { useAppDispatch } from '@/app/reduxHooks';
import { TransformationSchemaField } from '@/features/adp/services/common/common.types';
import { openOverlay } from '@/features/adp/state/overlaySlice';
import { Input } from '@/ui/input';
import { Label } from '@/ui/label';
import { Textarea } from '@/ui/textarea';
import { Button } from '@/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select';
import { MultiSelect } from '@/ui/multi-select';
import { RadioGroup, RadioGroupItem } from '@/ui/radio-group';
import { cn } from '@/shared/lib/utils';
import { Plus, X } from 'lucide-react';
import {
  buildDefaultRowFromGroupSubfields,
  getDataSourceOptions,
  resolveInputDefaultValue,
} from './transformationSheet.utils';

/** Runtime overrides for any `dataSource` key — passed down from the sheet. */
export type DynamicDataSources = Record<string, { label: string; value: string }[]>;

interface FieldProps {
  field: TransformationSchemaField;
  namePrefix: string;
  control: ReturnType<typeof useForm>['control'];
  register: ReturnType<typeof useForm>['register'];
  errors: Record<string, unknown>;
  disabled?: boolean;
  /** Live options that override the static STATIC_DATA_SOURCES map. */
  dynamicDataSources?: DynamicDataSources;
  /** Used to merge row state when `syncRowsFromDataSource` rebuilds the field array. */
  getValues?: UseFormGetValues<Record<string, unknown>>;
}

function FieldError({ name, errors }: Readonly<{ name: string; errors: Record<string, unknown> }>) {
  const error = (errors as Record<string, { message?: string }>)[name]?.message;
  if (!error) return null;
  return <p className="text-xs text-destructive mt-1">{error}</p>;
}

function FieldWrapper({ children, className }: Readonly<{ children: React.ReactNode; className?: string }>) {
  return <div className={cn('space-y-1.5', className)}>{children}</div>;
}

function FieldLabel({ field }: Readonly<{ field: TransformationSchemaField }>) {
  return (
    <Label>
      {field.label}
      {field.required && <span className="text-destructive ml-1">*</span>}
    </Label>
  );
}

function SelectField({ field, namePrefix, control, errors, disabled, dynamicDataSources }: Readonly<FieldProps>) {
  const options =
    field.options ??
    dynamicDataSources?.[field.dataSource ?? ''] ??
    getDataSourceOptions(field.dataSource, dynamicDataSources);
  const isDisabled = disabled || field.editable === false;
  const fieldName = `${namePrefix}${field.key}`;

  // Stable default: prefer explicit defaultValue, then first option if only one exists,
  // then empty string — never undefined — so the Select stays controlled from mount.
  const defaultVal =
    field.defaultValue ??
    (options.length === 1 ? options[0].value : '');

  return (
    <FieldWrapper>
      <FieldLabel field={field} />
      <Controller
        name={fieldName}
        control={control}
        defaultValue={defaultVal}
        render={({ field: f }) => {
          const selectedValue = typeof f.value === 'string' ? f.value : '';
          const selectRenderKey = `${fieldName}::${selectedValue}::${options.map(o => o.value).join('|')}`;
          return (
          <Select
            key={selectRenderKey}
            value={selectedValue}
            onValueChange={f.onChange}
            disabled={isDisabled}
          >
            <SelectTrigger>
              <SelectValue placeholder={`Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              {options.map(o => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          );
        }}
      />
      <FieldError name={fieldName} errors={errors} />
    </FieldWrapper>
  );
}

function MultiSelectField({ field, namePrefix, control, errors, disabled, dynamicDataSources }: Readonly<FieldProps>) {
  const options =
    field.options ??
    dynamicDataSources?.[field.dataSource ?? ''] ??
    getDataSourceOptions(field.dataSource, dynamicDataSources);
  const isDisabled = disabled || field.editable === false;
  const fieldName = `${namePrefix}${field.key}`;

  return (
    <FieldWrapper>
      <FieldLabel field={field} />
      <Controller
        name={fieldName}
        control={control}
        defaultValue={[]}
        render={({ field: f }) => {
          const selected: string[] = Array.isArray(f.value) ? f.value : [];
          return (
            <MultiSelect
              options={options}
              value={selected}
              onValueChange={f.onChange}
              disabled={isDisabled}
              placeholder={`Select ${field.label}`}
              hideSelectAll={options.length <= 3}
            />
          );
        }}
      />
      <FieldError name={fieldName} errors={errors} />
    </FieldWrapper>
  );
}

function DatasourcePickerField({ field, namePrefix, control, errors, disabled }: Readonly<FieldProps>) {
  const dispatch = useAppDispatch();
  const isDisabled = disabled || field.editable === false;
  const fieldName = `${namePrefix}${field.key}`;

  return (
    <FieldWrapper>
      <FieldLabel field={field} />
      <Controller
        name={fieldName}
        control={control}
        defaultValue={field.defaultValue ?? ''}
        render={({ field: f }) => (
          <div className="relative">
            <Input
              value={String(f.value ?? '')}
              readOnly
              disabled={isDisabled}
              placeholder={field.placeholder ?? 'No datasource selected'}
              className="pr-44"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isDisabled}
              className="absolute right-1 top-1/2 h-8 -translate-y-1/2 border border-input bg-background px-3 text-xs hover:bg-accent"
              onClick={() =>
                dispatch(
                  openOverlay({
                    type: 'transformation-datasource-picker',
                    data: {},
                  })
                )
              }
            >
              Choose Datasource
            </Button>
          </div>
        )}
      />
      <FieldError name={fieldName} errors={errors} />
    </FieldWrapper>
  );
}

function InputField({ field, namePrefix, register, errors, disabled }: Readonly<FieldProps>) {
  const isDisabled = disabled || field.editable === false;
  const fieldName = `${namePrefix}${field.key}`;
  const defaultValue = resolveInputDefaultValue(field);

  return (
    <FieldWrapper>
      <FieldLabel field={field} />
      <Input
        {...register(fieldName)}
        disabled={isDisabled}
        placeholder={field.placeholder}
        defaultValue={defaultValue}
      />
      <FieldError name={fieldName} errors={errors} />
    </FieldWrapper>
  );
}

function TextareaField({ field, namePrefix, register, errors, disabled }: Readonly<FieldProps>) {
  const isDisabled = disabled || field.editable === false;
  const fieldName = `${namePrefix}${field.key}`;

  return (
    <FieldWrapper>
      <FieldLabel field={field} />
      <Textarea
        {...register(fieldName)}
        disabled={isDisabled}
        placeholder={field.placeholder}
        rows={3}
        className="resize-none"
      />
      <FieldError name={fieldName} errors={errors} />
    </FieldWrapper>
  );
}

function RadioField({ field, namePrefix, control, errors, disabled }: Readonly<FieldProps>) {
  const isDisabled = disabled || field.editable === false;
  const fieldName = `${namePrefix}${field.key}`;

  return (
    <FieldWrapper>
      <FieldLabel field={field} />
      <Controller
        name={fieldName}
        control={control}
        render={({ field: f }) => (
          <RadioGroup
            value={f.value ?? ''}
            onValueChange={f.onChange}
            disabled={isDisabled}
            className="flex gap-4"
          >
            {(field.options ?? []).map(o => (
              <div key={o.value} className="flex items-center gap-2">
                <RadioGroupItem value={o.value} id={`${fieldName}-${o.value}`} />
                <Label htmlFor={`${fieldName}-${o.value}`} className="cursor-pointer font-normal">
                  {o.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        )}
      />
      <FieldError name={fieldName} errors={errors} />
    </FieldWrapper>
  );
}

function CodeEditorField({ field, namePrefix, control, errors, disabled }: Readonly<FieldProps>) {
  const fieldName = `${namePrefix}${field.key}`;
  const editorLanguage = field.language?.trim() || 'plaintext';
  const isDisabled = disabled || field.editable === false;

  return (
    <FieldWrapper>
      <FieldLabel field={field} />
      <Controller
        name={fieldName}
        control={control}
        defaultValue={field.defaultValue ?? ''}
        render={({ field: controlledField }) => (
          <div className="overflow-hidden rounded-md border border-input">
            <Editor
              height="220px"
              language={editorLanguage}
              value={String(controlledField.value ?? '')}
              onChange={(value) => controlledField.onChange(value ?? '')}
              options={{
                readOnly: isDisabled,
                minimap: { enabled: false },
                fontSize: 13,
                glyphMargin: false,
                lineNumbers: 'on',
                lineNumbersMinChars: 2,
                lineDecorationsWidth: 8,
                folding: false,
                wordWrap: 'on',
                scrollBeyondLastLine: false,
                automaticLayout: true,
              }}
              theme="vs-dark"
            />
          </div>
        )}
      />
      <FieldError name={fieldName} errors={errors} />
    </FieldWrapper>
  );
}

/**
 * Subscribes only to the dependency path for `visibleWhen` / `visibleIf`, so the
 * transformation sheet does not need `watch()` on the entire form (which
 * re-renders on every keystroke).
 */
function useSchemaFieldVisibility(
  field: TransformationSchemaField,
  namePrefix: string,
  control: ReturnType<typeof useForm>['control'],
): boolean {
  const condition = field.visibleWhen ?? field.visibleIf;
  const depPath = useMemo(() => {
    if (!condition) return undefined;
    const base = namePrefix.replace(/\.$/, '');
    return base ? `${base}.${condition.field}` : condition.field;
  }, [condition, namePrefix]);

  const depValue = useWatch({
    control,
    name: (depPath ?? '__schema_field_visibility__') as never,
    disabled: !depPath,
  });

  if (!condition) return true;
  return (depValue as unknown) === (condition.equals as unknown);
}

/** Safe key for matching synced rows (column name is always a string from the form). */
function syncRowLookupKey(value: unknown): string {
  if (typeof value === 'string') return value.toLowerCase();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value).toLowerCase();
  return '';
}

interface GroupFieldProps {
  field: TransformationSchemaField;
  namePrefix: string;
  control: ReturnType<typeof useForm>['control'];
  register: ReturnType<typeof useForm>['register'];
  errors: Record<string, unknown>;
  disabled?: boolean;
  dynamicDataSources?: DynamicDataSources;
  getValues?: UseFormGetValues<Record<string, unknown>>;
}

function GroupField({
  field,
  namePrefix,
  control,
  register,
  errors,
  disabled,
  dynamicDataSources,
  getValues,
}: Readonly<GroupFieldProps>) {
  const arrayName = `${namePrefix}${field.key}`;
  const { fields: rows, append, remove, replace } = useFieldArray({ control, name: arrayName });

  const defaultRow = () => buildDefaultRowFromGroupSubfields(field.fields ?? []);

  const syncDs = field.syncRowsFromDataSource;
  const minInitial = Math.max(field.initialRowCount ?? 0, field.minItems ?? 0);
  const syncOptions = syncDs ? (dynamicDataSources?.[syncDs] ?? []) : [];
  const syncSig = syncOptions.map(o => o.value).join('\u0001');

  const hasAutoAppendedRef = useRef(false);
  const hasMinSeededRef = useRef(false);

  useEffect(() => {
    if (disabled || !syncDs || !getValues) return;
    const valueKey = field.syncRowValueKey ?? 'columnName';
    const currentRaw = getValues(arrayName as never);
    const current = Array.isArray(currentRaw) ? (currentRaw as Record<string, unknown>[]) : [];
    const prevBy = new Map(
      current
        .filter((r): r is Record<string, unknown> => r != null && typeof r === 'object')
        .map(r => [syncRowLookupKey(r[valueKey]), r]),
    );
    const next = syncOptions.map(opt => {
      const row = defaultRow();
      row[valueKey] = opt.value;
      const old = prevBy.get(syncRowLookupKey(opt.value));
      if (old) {
        for (const sub of field.fields ?? []) {
          if (sub.key === valueKey) continue;
          const v = old[sub.key];
          if (v !== undefined && v !== '') row[sub.key] = v;
        }
      }
      return row;
    });
    replace(next);
  }, [
    disabled,
    syncDs,
    syncSig,
    arrayName,
    getValues,
    replace,
    field.syncRowValueKey,
    field.fields,
  ]);

  useEffect(() => {
    if (disabled || syncDs) return;
    if (minInitial > 0) {
      if (rows.length === 0 && !hasMinSeededRef.current) {
        hasMinSeededRef.current = true;
        replace(Array.from({ length: minInitial }, () => defaultRow()));
      }
      return;
    }
    if (field.repeatable && rows.length === 0 && !hasAutoAppendedRef.current) {
      hasAutoAppendedRef.current = true;
      append(defaultRow());
    }
  }, [
    disabled,
    syncDs,
    minInitial,
    field.repeatable,
    rows.length,
    append,
    replace,
    field.fields,
  ]);

  const minRowsForRemove =
    typeof field.minItems === 'number' && field.minItems > 0 ? field.minItems : 1;
  const canRemoveRow = field.allowRemove !== false && rows.length > minRowsForRemove;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{field.label}</Label>
        {!disabled && !field.hideAddButton && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => append(defaultRow())}
            className="h-auto py-0.5 px-2 text-xs text-primary"
          >
            <Plus className="h-3 w-3 mr-1" />
            {field.addButtonLabel ?? 'Add Row'}
          </Button>
        )}
      </div>
      <div className="space-y-2">
        {rows.length === 0 && (
          <p className="text-xs text-muted-foreground italic">No rows yet. Click add to start.</p>
        )}
        {rows.map((row, idx) => (
          <div key={row.id} className="rounded-md border border-border bg-muted/30 p-3 relative">
            <div className="space-y-2">
              {(field.fields ?? []).map(subField => (
                <RenderField
                  key={subField.key}
                  field={subField}
                  namePrefix={`${arrayName}.${idx}.`}
                  control={control}
                  register={register}
                  errors={errors}
                  disabled={disabled}
                  dynamicDataSources={dynamicDataSources}
                  getValues={getValues}
                />
              ))}
            </div>
            {!disabled && canRemoveRow && (
              <button
                type="button"
                onClick={() => remove(idx)}
                className="absolute top-2 right-2 text-muted-foreground hover:text-destructive transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function RenderField(props: Readonly<FieldProps>) {
  const { field, namePrefix, control } = props;
  const visible = useSchemaFieldVisibility(field, namePrefix, control);
  if (!visible) return null;

  switch (field.type) {
    case 'select':
      return <SelectField {...props} />;
    case 'multiselect':
      return <MultiSelectField {...props} />;
    case 'datasource_picker':
      return <DatasourcePickerField {...props} />;
    case 'input':
      return <InputField {...props} />;
    case 'textarea':
      return <TextareaField {...props} />;
    case 'radio':
      return <RadioField {...props} />;
    case 'code_editor':
      return <CodeEditorField {...props} />;
    case 'group':
      return (
        <GroupField
          field={field}
          namePrefix={props.namePrefix}
          control={props.control}
          register={props.register}
          errors={props.errors}
          disabled={props.disabled}
          dynamicDataSources={props.dynamicDataSources}
          getValues={props.getValues}
        />
      );
    default:
      return null;
  }
}
